import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { ChevronLeft, Download, Archive, ArchiveRestore } from "lucide-react";

import { TimerCard } from "./orders/detail/TimerCard";
import { CustomerVehicleCards } from "./orders/detail/CustomerVehicleCards";
import { TasksListCard } from "./orders/detail/TasksListCard";
import { SummaryCard } from "./orders/detail/SummaryCard";
import { ImageGalleryCard } from "./orders/detail/ImageGalleryCard";
import { AbschlusskontrolleCard } from "./orders/detail/AbschlusskontrolleCard";
import { CustomerDocumentsCard } from "./orders/detail/CustomerDocumentsCard";
import { AdminCostCard } from "./orders/detail/AdminCostCard";
import { CustomerEditDialog } from "./orders/detail/CustomerEditDialog";
import { VehicleEditDialog } from "./orders/detail/VehicleEditDialog";
import { ImageUploadDialog } from "./orders/detail/ImageUploadDialog";
import { computeOrderTotals } from "./orders/detail/helpers";

import { TaskFormDialog } from "./orders/TaskFormDialog";
import { ManualTimeDialog } from "./orders/ManualTimeDialog";
import { getStatusBadge } from "./orders/orderHelpers";

const OrderDetailSheet = ({
  order,
  open,
  isOpen,
  onOpenChange,
  onClose,
  onOrderUpdated,
  onOrderUpdate,
  customers = [],
  vehicles = []
}) => {
  // Support both legacy (isOpen/onClose/onOrderUpdate) and new (open/onOpenChange/onOrderUpdated) prop names
  const sheetOpen = open ?? isOpen ?? false;
  const handleOpenChange = (val) => {
    onOpenChange?.(val);
    if (!val) onClose?.();
  };
  const notifyOrderUpdate = (updated) => {
    onOrderUpdate?.(updated);
    onOrderUpdated?.(updated);
  };
  const navigate = useNavigate();
  const { user } = useAuth();
  const [localOrder, setLocalOrder] = useState(order);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  // Dialog states
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageRefreshCb, setImageRefreshCb] = useState(null);

  // Manual time entry state
  const [manualTimeOpen, setManualTimeOpen] = useState(false);
  const [manualTimeForm, setManualTimeForm] = useState({
    date: new Date().toISOString().split("T")[0],
    start_time: "08:00", end_time: "16:00", pause_minutes: 30, notes: ""
  });

  // Sync localOrder with prop
  useEffect(() => {
    if (order) {
      setLocalOrder(order);
      setCustomer(customers.find(c => c.id === order.customer_id) || null);
      setVehicle(vehicles.find(v => v.id === order.vehicle_id) || null);
    }
  }, [order, customers, vehicles]);

  // Fetch company settings once when sheet opens (for hourly rate / stundenlohn)
  useEffect(() => {
    if (!sheetOpen || companySettings) return;
    api.get("/api/company-settings")
      .then((res) => setCompanySettings(res.data))
      .catch(() => setCompanySettings({ stundenlohn: 0 }));
  }, [sheetOpen, companySettings]);

  const totals = useMemo(() => computeOrderTotals(localOrder), [localOrder]);

  const refreshOrder = async () => {
    try {
      const res = await api.get(`/api/work-orders/${localOrder.id}`);
      setLocalOrder(res.data);
      notifyOrderUpdate(res.data);
    } catch {
      // Silent: caller already showed feedback for primary action
    }
  };

  const updateOrderState = (updated) => {
    setLocalOrder(updated);
    notifyOrderUpdate(updated);
  };

  // Task handlers
  const handleToggleTask = async (taskId, completed) => {
    try {
      await api.put(`/api/work-orders/${localOrder.id}/tasks/${taskId}/full`, { completed: !completed });
      await refreshOrder();
    } catch {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleEditTask = (task) => { setEditingTask(task); setTaskDialogOpen(true); };
  const handleAddTask = () => { setEditingTask(null); setTaskDialogOpen(true); };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Aufgabe wirklich löschen?")) return;
    try {
      await api.delete(`/api/work-orders/${localOrder.id}/tasks/${taskId}`);
      toast.success("Aufgabe gelöscht");
      await refreshOrder();
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/api/work-orders/${localOrder.id}`, { status: newStatus });
      toast.success("Status aktualisiert");
      await refreshOrder();
    } catch {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Diesen Auftrag ins Archiv verschieben?")) return;
    try {
      await api.post(`/api/work-orders/${localOrder.id}/archive`);
      toast.success("Auftrag archiviert");
      handleOpenChange(false);
      onOrderUpdated?.({ ...localOrder, archived: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archivieren fehlgeschlagen");
    }
  };

  const handleUnarchive = async () => {
    try {
      await api.post(`/api/work-orders/${localOrder.id}/unarchive`);
      toast.success("Auftrag wiederhergestellt");
      handleOpenChange(false);
      onOrderUpdated?.({ ...localOrder, archived: false });
    } catch {
      toast.error("Wiederherstellen fehlgeschlagen");
    }
  };

  // Image upload handlers
  const handleImageUploadRequest = (preview, refreshCb) => {
    setImagePreview(preview);
    setImageRefreshCb(() => refreshCb);
    setImageDialogOpen(true);
  };
  const handleImageUploaded = () => { imageRefreshCb?.(); setImagePreview(null); };

  // Manual time entry
  const openManualTime = () => {
    setManualTimeForm({
      date: new Date().toISOString().split("T")[0],
      start_time: "08:00", end_time: "16:00", pause_minutes: 30, notes: ""
    });
    setManualTimeOpen(true);
  };
  const submitManualTime = async () => {
    if (!localOrder?.id) return;
    try {
      const res = await api.post(`/api/work-orders/${localOrder.id}/time/manual`, manualTimeForm);
      updateOrderState(res.data);
      setManualTimeOpen(false);
      toast.success("Zeiteintrag nachgetragen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Nachtragen");
    }
  };

  if (!localOrder) return null;

  return (
    <>
      <Sheet open={sheetOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl lg:max-w-4xl p-0 flex flex-col"
          data-testid="order-detail-sheet"
        >
          {/* Fixed Header */}
          <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Button variant="ghost" size="icon" onClick={() => handleOpenChange(false)} className="shrink-0 sm:hidden">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold truncate" style={{ fontFamily: 'Barlow Condensed' }}>
                    {localOrder.description || "Auftrag"}
                  </h2>
                  {localOrder.auftragsnummer && (
                    <p className="text-sm text-muted-foreground">#{localOrder.auftragsnummer}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {getStatusBadge(localOrder.status)}
                {localOrder.archived ? (
                  <Button
                    variant="outline" size="sm"
                    onClick={handleUnarchive}
                    className="hidden sm:flex"
                    data-testid="detail-unarchive-btn"
                  >
                    <ArchiveRestore className="w-4 h-4 mr-2" />Wiederherstellen
                  </Button>
                ) : localOrder.status === "erledigt" ? (
                  <Button
                    variant="outline" size="sm"
                    onClick={handleArchive}
                    className="hidden sm:flex"
                    data-testid="detail-archive-btn"
                  >
                    <Archive className="w-4 h-4 mr-2" />Archivieren
                  </Button>
                ) : null}
                <Button
                  variant="outline" size="sm"
                  onClick={() => navigate(`/pdf-export?order=${localOrder.id}`)}
                  className="hidden sm:flex"
                  data-testid="detail-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />Arbeitskarte PDF
                </Button>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <ScrollArea className="flex-1">
            <div className="p-4 sm:p-6 space-y-6">
              <TimerCard
                order={localOrder}
                totals={totals}
                onOrderUpdate={updateOrderState}
                onOpenManualTime={openManualTime}
              />
              <CustomerVehicleCards
                customer={customer}
                vehicle={vehicle}
                onEditCustomer={() => setCustomerDialogOpen(true)}
                onEditVehicle={() => setVehicleDialogOpen(true)}
              />
              <CustomerDocumentsCard customerId={customer?.id} />
              <TasksListCard
                order={localOrder}
                totals={totals}
                onToggleTask={handleToggleTask}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                onAddTask={handleAddTask}
              />
              <SummaryCard order={localOrder} totals={totals} onStatusChange={handleStatusChange} companySettings={companySettings} />
              <AdminCostCard order={localOrder} user={user} totals={totals} companySettings={companySettings} />
              <ImageGalleryCard orderId={localOrder.id} onRequestUpload={handleImageUploadRequest} />
              <AbschlusskontrolleCard order={localOrder} onOrderUpdate={updateOrderState} />
            </div>
          </ScrollArea>

          {/* Fixed Footer for Mobile */}
          <div className="sticky bottom-0 bg-background border-t p-4 sm:hidden">
            <Button className="w-full gap-2" onClick={() => navigate(`/pdf-export?order=${localOrder.id}`)}>
              <Download className="w-4 h-4" />Arbeitskarte PDF
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Task Add/Edit Dialog (reuses TaskFormDialog) */}
      <TaskFormDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        orderId={localOrder.id}
        editTask={editingTask}
        onSuccess={refreshOrder}
      />

      {/* Customer Edit Dialog */}
      <CustomerEditDialog
        open={customerDialogOpen}
        onOpenChange={setCustomerDialogOpen}
        customer={customer}
        onUpdated={setCustomer}
      />

      {/* Vehicle Edit Dialog */}
      <VehicleEditDialog
        open={vehicleDialogOpen}
        onOpenChange={setVehicleDialogOpen}
        vehicle={vehicle}
        onUpdated={setVehicle}
      />

      {/* Image Upload Dialog */}
      <ImageUploadDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        orderId={localOrder.id}
        preview={imagePreview}
        onUploaded={handleImageUploaded}
      />

      {/* Manual Time (Nachtrag) Dialog */}
      <ManualTimeDialog
        open={manualTimeOpen}
        onOpenChange={setManualTimeOpen}
        form={manualTimeForm}
        setForm={setManualTimeForm}
        onSubmit={submitManualTime}
      />
    </>
  );
};

export default OrderDetailSheet;
